// src/modules/app-views/products/pages/Document/tabs/SuppliersTab/modals/CostItemModal/index.jsx
// *******************************************************************************
// COST ITEM MODAL
// *******************************************************************************
// GenericModal (1 step) for adding or editing a cost/tax item
// in the supplier cost composition table.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo } from 'react';
import { DollarOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import financialService from 'services/financialService';
import Step1 from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CostItemModal = ({
  show,
  setShow,
  onConfirm,
  editingItem,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(async ({ form }) => {
    const values = form.getFieldsValue(true);

    const costData = {
      name: (values.name || '').toUpperCase().trim(),
      mode: values.mode || 'percentage',
      percentage: financialService.round(values.percentage || 0, 3),
      value: financialService.roundCurrency(values.value || 0),
      type: values.type || 'cost',
      payerSource: values.payerSource || 'other',
    };

    onConfirm?.(costData);
    return true;
  }, [onConfirm]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => ({
    name: editingItem?.name || '',
    mode: editingItem?.mode || 'percentage',
    percentage: editingItem?.percentage || 0,
    value: editingItem?.value || 0,
    type: editingItem?.type || 'cost',
    payerSource: editingItem?.payerSource || 'other',
  }), [editingItem]);

  const steps = useMemo(() => [
    {
      stepTitle: 'cost',
      validationFields: ['name', 'type'],
      content: () => <Step1 />,
      onEnter: ({ goFocus }) => goFocus?.('name'),
    },
  ], []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={editingItem ? 'edit' : 'add_cost'}
      icon={<DollarOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={560}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CostItemModal;
